import * as React from "react";

import {
    PhysicsModelName,
    ValidPhysicsModelNames,
} from "@volley/physics/dist/models";

import logger from "../../log";
import type { Sport } from "../common/context/sport";
import { useSelectedSport } from "../common/context/sport";

import { useStatus } from "./status";

export const PHYSICS_MODEL_NAME_PREFIX = "physics-";
export const PHYSICS_MODEL_DEFAULT = "20B-base";
export const PICKLEBALL_PHYSICS_MODEL_DEFAULT = "30B-base";

/*
    The model to assume for a sport when nothing more specific is known.

    Per sport rather than one global default because the RPM->MPH tables are
    only populated for the sports a model was fitted against: the 20* models
    carry platform tennis, padel and tennis and leave PICKLEBALL an empty
    array, and the 30* models carry only pickleball (tableRPM2MPH.ts). Handing
    pickleball a 20* model therefore does not assert — every sport key exists —
    it silently reads past the end of an empty table and yields NaN speeds.
*/
export const DEFAULT_PHYSICS_MODEL_BY_SPORT: Record<Sport, PhysicsModelName> = {
    PLATFORM_TENNIS:
        `${PHYSICS_MODEL_NAME_PREFIX}${PHYSICS_MODEL_DEFAULT}` as PhysicsModelName,
    PADEL: `${PHYSICS_MODEL_NAME_PREFIX}${PHYSICS_MODEL_DEFAULT}` as PhysicsModelName,
    TENNIS: `${PHYSICS_MODEL_NAME_PREFIX}${PHYSICS_MODEL_DEFAULT}` as PhysicsModelName,
    PICKLEBALL:
        `${PHYSICS_MODEL_NAME_PREFIX}${PICKLEBALL_PHYSICS_MODEL_DEFAULT}` as PhysicsModelName,
    // Lacrosse throws a platform tennis ball from a model-20 head, so it takes
    // the default model - matching what PhysicsModelProvider hands the app.
    LACROSSE:
        `${PHYSICS_MODEL_NAME_PREFIX}${PHYSICS_MODEL_DEFAULT}` as PhysicsModelName,
};

/**
 * Narrow a stored `physics_model_name` to a model the physics tables actually
 * know, falling back to the sport's default.
 *
 * The column is a bare `String`, so a `as PhysicsModelName` cast at the read
 * site proves nothing. Two kinds of row do not carry a usable name:
 * experience-backed plays, whose shadow `workout_apps` row is written with `""`
 * on purpose (the model that ran is pinned per run in the journal, not here),
 * and old rows naming a model since retired from `ValidPhysicsModelNames`.
 * Both used to reach `setPhysicsModel` and trip its assert mid-render.
 */
export function resolvePhysicsModelName(
    name: string | null | undefined,
    sport: Sport,
): PhysicsModelName {
    return ValidPhysicsModelNames.includes(name as PhysicsModelName)
        ? (name as PhysicsModelName)
        : DEFAULT_PHYSICS_MODEL_BY_SPORT[sport];
}

export interface PhysicsModelContext {
    physicsModelName: PhysicsModelName;
}

export const PHYSICS_MODEL_CONTEXT_DEFAULT: PhysicsModelContext = {
    physicsModelName: (PHYSICS_MODEL_NAME_PREFIX +
        PHYSICS_MODEL_DEFAULT) as PhysicsModelName,
};

export const physicsModelContext = React.createContext<PhysicsModelContext>(
    PHYSICS_MODEL_CONTEXT_DEFAULT,
);

export const PhysicsModelProvider = React.memo(
    ({ children }: React.PropsWithChildren) => {
        const { selected: selectedSport } = useSelectedSport();
        const baseModel = DEFAULT_PHYSICS_MODEL_BY_SPORT[selectedSport];
        const [physicsModel, setPhysicsModel] = React.useState({
            physicsModelName: baseModel,
        });

        React.useEffect(() => {
            setPhysicsModel({ physicsModelName: baseModel });
        }, [baseModel]);

        const { status } = useStatus();

        React.useEffect(() => {
            if (
                status?.physics &&
                status.physics.modelName !== physicsModel.physicsModelName
            ) {
                logger.info(
                    `Setting physics model to ${status.physics.modelName} from Coach Status`,
                );
                setPhysicsModel({
                    physicsModelName: status.physics
                        .modelName as PhysicsModelName,
                });
            }
        }, [status?.physics, physicsModel.physicsModelName]);

        return (
            <physicsModelContext.Provider value={physicsModel}>
                {children}
            </physicsModelContext.Provider>
        );
    },
);

PhysicsModelProvider.displayName = "PhysicsModelProvider";

export function usePhysicsModelContext(): PhysicsModelContext {
    return React.useContext(physicsModelContext);
}
